import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2 } from "lucide-react";
import { WelcomeStep } from "@/components/application/WelcomeStep";
import { ContactDetailsStep } from "@/components/application/ContactDetailsStep";
import { ExperienceStep } from "@/components/application/ExperienceStep";
import { SkillsStep } from "@/components/application/SkillsStep";
import { ReviewStep } from "@/components/application/ReviewStep";

export default function JobApplication() {
  const navigate = useNavigate();
  const location = useLocation();
  const jobData = location.state?.job;

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    contactDetails: {
      fullName: "",
      email: "",
      phone: "",
    },
    experience: {
      type: "", // 'upload', 'build', 'paste'
      cvFile: null as File | null,
      pastedText: "",
      workHistory: [] as Array<{
        jobTitle: string;
        company: string;
        duration: string;
        description: string;
      }>,
      skills: [] as string[],
    },
    skillsText: "",
    confirmed: false,
  });

  const updateFormData = (section: string, data: any) => {
    setFormData((prev) => ({
      ...prev,
      [section]: data,
    }));
  };

  const nextStep = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = () => {
    // Here you would submit the application to your backend
    console.log("Application submitted:", formData);
    setCurrentStep(5); // Move to confirmation
  };

  if (!jobData) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-24 pb-16 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6">
              <p className="text-center text-muted-foreground mb-4">
                No job selected. Please return to the job search page.
              </p>
              <Button onClick={() => navigate("/job-search")} className="w-full">
                Browse Jobs
              </Button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  const steps = [
    "Welcome",
    "Your Details",
    "Your Experience",
    "Your Skills",
    "Review & Submit",
    "Confirmation",
  ];

  const progressPercentage = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="min-h-screen flex flex-col bg-muted/30">
      <Navbar />

      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-3xl">
          {/* Progress Indicator */}
          {currentStep > 0 && currentStep < 5 && (
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-medium text-muted-foreground">
                  Step {currentStep} of 4
                </p>
                <p className="text-sm font-medium text-primary">
                  {Math.round(((currentStep) / 4) * 100)}% Complete
                </p>
              </div>
              <Progress value={(currentStep / 4) * 100} className="h-2" />
            </div>
          )}

          {/* Step Content */}
          <Card className="shadow-lg">
            {currentStep === 0 && (
              <WelcomeStep job={jobData} onNext={nextStep} />
            )}

            {currentStep === 1 && (
              <ContactDetailsStep
                data={formData.contactDetails}
                onUpdate={(data) => updateFormData("contactDetails", data)}
                onNext={nextStep}
                onBack={previousStep}
              />
            )}

            {currentStep === 2 && (
              <ExperienceStep
                data={formData.experience}
                onUpdate={(data) => updateFormData("experience", data)}
                onNext={nextStep}
                onBack={previousStep}
              />
            )}

            {currentStep === 3 && (
              <SkillsStep
                data={formData.skillsText}
                onUpdate={(data) => updateFormData("skillsText", data)}
                onNext={nextStep}
                onBack={previousStep}
              />
            )}

            {currentStep === 4 && (
              <ReviewStep
                formData={formData}
                job={jobData}
                onBack={previousStep}
                onSubmit={handleSubmit}
              />
            )}

            {currentStep === 5 && (
              <CardContent className="pt-12 pb-12 text-center">
                <div className="flex justify-center mb-6">
                  <div className="rounded-full bg-primary/10 p-4">
                    <CheckCircle2 className="h-16 w-16 text-primary" />
                  </div>
                </div>
                <h2 className="text-3xl font-bold mb-4">
                  Your application has been sent!
                </h2>
                <p className="text-lg text-muted-foreground mb-2">
                  The employer will be in touch if you are a good match.
                </p>
                <p className="text-xl font-semibold text-primary mb-8">
                  You've got this! 💪
                </p>
                <div className="flex gap-4 justify-center flex-wrap">
                  <Button onClick={() => navigate("/job-search")}>
                    Browse More Jobs
                  </Button>
                  <Button variant="outline" onClick={() => navigate("/")}>
                    Return Home
                  </Button>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
}
